import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useThrottleCooldown — tracks an active 429 cooldown.
 *
 * Returns `{ secondsLeft, isCoolingDown, start, label }`:
 *   - secondsLeft  → number, 0 when not throttled
 *   - isCoolingDown → boolean
 *   - start(seconds) → call when an API throws with `code === "throttled"`
 *   - label(prefix)  → "Try again in 38 seconds" / "Try again in 2 min"
 *
 * One-second tick interval, cleaned up on unmount. The hook is local
 * to one form — different forms (login vs resend-verification) get
 * independent cooldowns.
 */
export default function useThrottleCooldown() {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const tickRef = useRef(null);

  useEffect(() => () => {
    if (tickRef.current) clearInterval(tickRef.current);
  }, []);

  const start = useCallback((seconds) => {
    const n = Math.max(1, Math.floor(Number(seconds) || 0));
    setSecondsLeft(n);
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(tickRef.current);
          tickRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, []);

  const label = useCallback(
    (prefix = "Try again in") => {
      if (secondsLeft <= 0) return "";
      if (secondsLeft >= 60) {
        const m = Math.ceil(secondsLeft / 60);
        return `${prefix} ${m} min`;
      }
      return `${prefix} ${secondsLeft}s`;
    },
    [secondsLeft],
  );

  return {
    secondsLeft,
    isCoolingDown: secondsLeft > 0,
    start,
    label,
  };
}
